import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { CurrentUser, DealExcludedLender, TermSheetTypes, TermSheetTypesMap } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../../shared/utils/date';

interface AddTermSheetFormProps {
	dataRoomGuid: string | undefined;
	isAdding: boolean;
	currentUser: CurrentUser;
	availableLenders: DealExcludedLender[];
	close: () => void;
}

export const AddExcludedLenderTermSheetForm: React.FC<AddTermSheetFormProps> = ({
	dataRoomGuid,
	isAdding,
	currentUser,
	availableLenders,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const addSheetMutation = useMutation(
		async ({ values }: AddTermSheetFormMutationVariables) => {
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();
				formData.append('bankGuid', values.bankGuid);
				formData.append('dateReceived', values.dateReceived.toISOString());
				formData.append('isIncumbentSolution', values.isIncumbentSolution ? 'true' : 'false');
				formData.append('notifyBorrower', values.notifyBorrower ? 'true' : 'false');
				formData.append('termSheetType', values.termSheetType);

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<void>(
					api.post(CerebroApiEndpoints.TermSheets.AddExcludedLender(dataRoomGuid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Term sheet was added.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.List(dataRoomGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={isAdding} onClose={close}>
			<CerebroHeading>Add Term Sheet</CerebroHeading>

			<Formik
				initialValues={{
					isIncumbentSolution: false,
					bankGuid: '',
					notifyBorrower: false,
					dateReceived: new Date(),
					termSheetType: 'TermSheet' as TermSheetTypes,
					uploads: [] as File[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(addSheetMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="bankGuid" required>
										Excluded Lender
									</CerebroLabel>
									<Field
										id="bankGuid"
										as={CerebroDropdown}
										name="bankGuid"
										disabled={isSubmitting}
										error={touched.bankGuid && errors.bankGuid !== undefined}
									>
										{availableLenders.map(lender => (
											<CerebroDropdownMenuItem key={lender.bankGuid} value={lender.bankGuid}>
												{lender.bankName}
											</CerebroDropdownMenuItem>
										))}
									</Field>
									{errors.bankGuid && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.bankGuid}
										</CerebroText>
									)}
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="termSheetType" required>
										Term Sheet Type
									</CerebroLabel>
									<Field
										id="termSheetType"
										as={CerebroDropdown}
										name="termSheetType"
										disabled={isSubmitting}
									>
										{Object.entries(TermSheetTypesMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel required id="uploads">
										Term Sheet File
									</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										onlyPdf={true}
										multiple={false}
										sx={{ marginY: 3, width: '100%' }}
										error={touched.uploads && errors.uploads !== undefined}
									/>
									{errors.uploads && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.uploads}
										</CerebroText>
									)}
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="dateReceived" required>
										Date Received
									</CerebroLabel>
									<Field
										id="dateReceived"
										name="dateReceived"
										component={CerebroDatePicker}
										disabled={isSubmitting}
										error={touched.dateReceived && errors.dateReceived !== undefined}
									/>
									{touched.dateReceived && errors.dateReceived && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.dateReceived}
										</CerebroText>
									)}
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="isIncumbentSolution">Incumbent Solution</CerebroLabel>
									<Field
										id="isIncumbentSolution"
										as={CerebroYesNo}
										name="isIncumbentSolution"
										disabled={isSubmitting}
									/>
								</Grid>
								{currentUser.isEmployeeUser && (
									<Grid item xs={11}>
										<CerebroLabel id="notifyBorrower">Notify Borrower</CerebroLabel>
										<Field
											id="notifyBorrower"
											as={CerebroYesNo}
											name="notifyBorrower"
											disabled={isSubmitting}
										/>
									</Grid>
								)}
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddTermSheetFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddTermSheetFormValues>;
interface AddTermSheetFormValues {
	isIncumbentSolution: boolean;
	bankGuid: string;
	notifyBorrower: boolean;
	dateReceived: Date;
	termSheetType: TermSheetTypes;
	uploads: File[];
}
type AddTermSheetFormValueErrors = FormikErrors<AddTermSheetFormValues>;

function validateForm(values: AddTermSheetFormValues) {
	const errors: AddTermSheetFormValueErrors = {};

	if (!values.bankGuid || values.bankGuid === '') {
		errors.bankGuid = 'You must select a provider.';
	}

	if (!values.uploads || values.uploads.length == 0) {
		errors.uploads = 'You must select a term sheet file.';
	}

	if (!values.dateReceived) {
		errors.dateReceived = 'You must select a valid date the term sheet was received.';
	} else {
		const dateReceivedError = validateDateWithinPlusMinus100Years(values.dateReceived, 'Date Received');
		if (dateReceivedError) {
			errors.dateReceived = dateReceivedError;
		}
	}

	return errors;
}
